import { Feather } from "@expo/vector-icons";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type DropdownOption = {
  value: string;
  label: string;
  icon?: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  testID?: string;
  iconOnlyTrigger?: boolean;
  ariaLabel?: string;
};

export default function Dropdown({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className = "",
  triggerClassName = "",
  testID,
  iconOnlyTrigger = false,
  ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.value === value);

  const updateRect = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScrollOrResize = () => updateRect();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateRect]);

  const listbox =
    open && rect && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              minWidth: rect.width,
              zIndex: 2147483647,
            }}
            className="max-h-72 overflow-y-auto bg-white border border-gray-500 rounded-xl shadow-lg list-none p-1 m-0"
          >
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-base ${
                    isSelected
                      ? "bg-blue-50 text-blue-900"
                      : "hover:bg-gray-50 text-gray-900"
                  }`}
                >
                  {o.icon && <span>{o.icon}</span>}
                  <span className="whitespace-nowrap">{o.label}</span>
                </li>
              );
            })}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${className}`}
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid={testID}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white border-gray-500 text-base text-gray-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 ${triggerClassName}`}
      >
        {selected ? (
          <>
            {selected.icon && (
              <span className="flex-shrink-0">{selected.icon}</span>
            )}
            {!iconOnlyTrigger && (
              <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {selected.label}
              </span>
            )}
          </>
        ) : (
          <span className="text-gray-500 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
            {placeholder}
          </span>
        )}
        <Feather name="chevron-down" size={16} color="#6B7280" />
      </button>
      {listbox}
    </div>
  );
}
